import React, { useState, useEffect } from "react";
import {
  FaCalendarCheck,
  FaClock,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaSearch,
  FaFilter,
  FaEdit,
  FaTrash,
  FaCheck,
  FaTimes,
  FaEye,
  FaCalendarAlt,
  FaExclamationCircle,
  FaVideo,
} from "react-icons/fa";
import {
  getAllAppointments,
  updateAppointmentStatus,
  deleteAppointment,
  completeAppointmentWithDetails,
} from "../utils/emailUtils";
import { getStudentDocuments } from "../firebase";
import { logAdminActivity } from "../utils/adminSecurity";
import AppointmentCompletionModal from "./AppointmentCompletionModal";
import { ToastContainer } from "./Toast";
import "./AppointmentTracker.css";

const AppointmentTracker = () => {
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [currentAppointments, setCurrentAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPastAppointments, setShowPastAppointments] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [appointmentToComplete, setAppointmentToComplete] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [studentDocuments, setStudentDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const statusOptions = [
    { value: "all", label: "Tüm Durumlar", color: "#718096" },
    { value: "confirmed", label: "Onaylandı", color: "#48bb78" },
    { value: "cancelled", label: "İptal Edildi", color: "#e53e3e" },
    { value: "pending", label: "Bekliyor", color: "#ed8936" },
  ];

  const dateFilterOptions = [
    { value: "all", label: "Tüm Tarihler" },
    { value: "today", label: "Bugün" },
    { value: "tomorrow", label: "Yarın" },
    { value: "this_week", label: "Bu Hafta" },
    { value: "next_week", label: "Gelecek Hafta" },
    { value: "this_month", label: "Bu Ay" },
  ];

  useEffect(() => {
    loadAppointments();
  }, []);

  useEffect(() => {
    filterAppointments();
  }, [appointments, searchTerm, statusFilter, dateFilter]);

  const loadAppointments = async () => {
    setIsLoading(true);
    try {
      const appointmentData = await getAllAppointments();
      setAppointments(appointmentData);
    } catch (error) {
      console.error("Error loading appointments:", error);
      addToast("Randevular yüklenirken hata oluştu.", "error");
    }
    setIsLoading(false);
  };

  const filterAppointments = () => {
    // First, exclude completed appointments from the main list
    let filtered = appointments.filter(
      (appointment) => appointment.status !== "completed"
    );

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (appointment) =>
          appointment.userInfo.firstName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          appointment.userInfo.lastName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          appointment.userInfo.email
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          appointment.userInfo.phone.includes(searchTerm)
      );
    }

    // Status filter (excluding completed since they're already filtered out)
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (appointment) => appointment.status === statusFilter
      );
    }

    // Get current date in Turkey time for comparison
    const now = new Date();
    const turkeyTime = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    const today = new Date(
      turkeyTime.getUTCFullYear(),
      turkeyTime.getUTCMonth(),
      turkeyTime.getUTCDate()
    );

    // Separate current and past appointments
    const current = [];
    const past = [];

    filtered.forEach((appointment) => {
      const appointmentDate = new Date(appointment.date);
      const appointmentDay = new Date(
        appointmentDate.getFullYear(),
        appointmentDate.getMonth(),
        appointmentDate.getDate()
      );

      if (appointmentDay >= today) {
        current.push(appointment);
      } else {
        past.push(appointment);
      }
    });

    // Apply date filter only to current appointments
    let filteredCurrent = current;
    if (dateFilter !== "all") {
      filteredCurrent = current.filter((appointment) => {
        const appointmentDate = new Date(appointment.date);
        const appointmentDay = new Date(
          appointmentDate.getFullYear(),
          appointmentDate.getMonth(),
          appointmentDate.getDate()
        );

        switch (dateFilter) {
          case "today":
            return appointmentDay.getTime() === today.getTime();
          case "tomorrow":
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            return appointmentDay.getTime() === tomorrow.getTime();
          case "this_week":
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            return appointmentDay >= weekStart && appointmentDay <= weekEnd;
          case "next_week":
            const nextWeekStart = new Date(today);
            nextWeekStart.setDate(today.getDate() - today.getDay() + 7);
            const nextWeekEnd = new Date(nextWeekStart);
            nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
            return (
              appointmentDay >= nextWeekStart && appointmentDay <= nextWeekEnd
            );
          case "this_month":
            return (
              appointmentDate.getMonth() === now.getMonth() &&
              appointmentDate.getFullYear() === now.getFullYear()
            );
          default:
            return true;
        }
      });
    }

    // Sort current appointments - newest booking first (by creation time if available, otherwise by appointment date)
    filteredCurrent.sort((a, b) => {
      // If we have creation timestamps, use those (newest first)
      if (a.createdAt && b.createdAt) {
        const createdA = a.createdAt.toDate
          ? a.createdAt.toDate()
          : new Date(a.createdAt);
        const createdB = b.createdAt.toDate
          ? b.createdAt.toDate()
          : new Date(b.createdAt);
        return createdB - createdA;
      }

      // Otherwise sort by appointment date and time (nearest first)
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      return a.time.localeCompare(b.time);
    });

    // Sort past appointments - most recent first
    past.sort((a, b) => {
      // If we have creation timestamps, use those (newest first)
      if (a.createdAt && b.createdAt) {
        const createdA = a.createdAt.toDate
          ? a.createdAt.toDate()
          : new Date(a.createdAt);
        const createdB = b.createdAt.toDate
          ? b.createdAt.toDate()
          : new Date(b.createdAt);
        return createdB - createdA;
      }

      // Otherwise sort by appointment date (most recent first)
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB - dateA;
      }
      return b.time.localeCompare(a.time);
    });

    setCurrentAppointments(filteredCurrent);
    setPastAppointments(past);
    setFilteredAppointments(filteredCurrent); // Keep for backward compatibility
  };

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    setIsLoading(true);
    try {
      const appointment = appointments.find((app) => app.id === appointmentId);
      const oldStatus = appointment?.status;

      await updateAppointmentStatus(appointmentId, newStatus);

      setAppointments((prev) =>
        prev.map((appointment) =>
          appointment.id === appointmentId
            ? { ...appointment, status: newStatus }
            : appointment
        )
      );

      // Log the status change
      logAdminActivity("appointment_status_change", {
        appointmentId,
        oldStatus,
        newStatus,
        appointmentDate: appointment?.date,
        appointmentTime: appointment?.time,
        studentEmail:
          appointment?.userData?.email || appointment?.userInfo?.email,
        studentName: `${
          appointment?.userData?.firstName || appointment?.userInfo?.firstName
        } ${
          appointment?.userData?.lastName || appointment?.userInfo?.lastName
        }`,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error updating appointment status:", error);
      addToast("Randevu durumu güncellenirken hata oluştu.", "error");
    }
    setIsLoading(false);
  };

  // Toast functions
  const addToast = (message, type = "success", duration = 4000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    setToasts((prev) => [...prev, newToast]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // Handle completion with details
  const handleCompleteWithDetails = (appointment) => {
    setAppointmentToComplete(appointment);
    setShowCompletionModal(true);
  };

  const handleCompletionSubmit = async (appointmentId, completionData) => {
    setIsLoading(true);
    try {
      await completeAppointmentWithDetails(appointmentId, completionData);

      // Update local state
      setAppointments((prev) =>
        prev.map((appointment) =>
          appointment.id === appointmentId
            ? {
                ...appointment,
                status: "completed",
                completionDetails: completionData,
              }
            : appointment
        )
      );

      setShowCompletionModal(false);
      setAppointmentToComplete(null);

      // Show success toast
      addToast(
        "Randevu başarıyla tamamlandı ve detaylar kaydedildi!",
        "success"
      );
    } catch (error) {
      console.error("Error completing appointment with details:", error);
      addToast("Randevu tamamlanırken hata oluştu: " + error.message, "error");
    }
    setIsLoading(false);
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm("Bu randevuyu silmek istediğinizden emin misiniz?")) {
      return;
    }

    setIsLoading(true);
    try {
      const appointment = appointments.find((app) => app.id === appointmentId);

      await deleteAppointment(appointmentId);

      setAppointments((prev) =>
        prev.filter((appointment) => appointment.id !== appointmentId)
      );

      // Log the deletion
      logAdminActivity("appointment_deletion", {
        appointmentId,
        appointmentDate: appointment?.date,
        appointmentTime: appointment?.time,
        appointmentStatus: appointment?.status,
        studentEmail:
          appointment?.userData?.email || appointment?.userInfo?.email,
        studentName: `${
          appointment?.userData?.firstName || appointment?.userInfo?.firstName
        } ${
          appointment?.userData?.lastName || appointment?.userInfo?.lastName
        }`,
        studentPhone:
          appointment?.userData?.phone || appointment?.userInfo?.phone,
        timestamp: new Date().toISOString(),
        reason: "admin_manual_deletion",
      });
    } catch (error) {
      console.error("Error deleting appointment:", error);
      addToast("Randevu silinirken hata oluştu.", "error");
    }
    setIsLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return "Tarih belirtilmemiş";

    // Handle different date formats
    let dateObj;
    if (date.toDate && typeof date.toDate === "function") {
      // Firestore Timestamp
      dateObj = date.toDate();
    } else if (typeof date === "string") {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return "Geçersiz tarih";
    }

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return "Geçersiz tarih";
    }

    return dateObj.toLocaleDateString("tr-TR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatShortDate = (date) => {
    if (!date) return "Tarih yok";

    // Handle different date formats
    let dateObj;
    if (date.toDate && typeof date.toDate === "function") {
      // Firestore Timestamp
      dateObj = date.toDate();
    } else if (typeof date === "string") {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return "Geçersiz";
    }

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return "Geçersiz";
    }

    return dateObj.toLocaleDateString("tr-TR");
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(
      (option) => option.value === status
    );
    return statusOption ? statusOption.color : "#718096";
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(
      (option) => option.value === status
    );
    return statusOption ? statusOption.label : status;
  };

  const getEducationLabel = (education) => {
    const educationMap = {
      "lisans-ogrenci": "Lisans Öğrencisi",
      "lisans-mezunu": "Lisans Mezunu",
      "12-sinif": "12. Sınıf",
      diger: "Diğer",
    };
    return educationMap[education] || education || "Belirtilmemiş";
  };

  const openAppointmentModal = async (appointment) => {
    setSelectedAppointment(appointment);
    setShowModal(true);

    // Load documents if student number exists
    if (appointment.ogrenciNumarasi) {
      setLoadingDocuments(true);
      try {
        const docs = await getStudentDocuments(appointment.ogrenciNumarasi);
        setStudentDocuments(docs);
      } catch (error) {
        console.error("Error loading documents:", error);
        setStudentDocuments(null);
      } finally {
        setLoadingDocuments(false);
      }
    } else {
      setStudentDocuments(null);
    }
  };

  const closeAppointmentModal = () => {
    setSelectedAppointment(null);
    setShowModal(false);
    setStudentDocuments(null);
  };

  const getAppointmentStats = () => {
    // Only count non-completed appointments
    const activeAppointments = appointments.filter(
      (a) => a.status !== "completed"
    );
    const total = activeAppointments.length;
    const confirmed = activeAppointments.filter(
      (a) => a.status === "confirmed"
    ).length;
    const cancelled = activeAppointments.filter(
      (a) => a.status === "cancelled"
    ).length;
    const pending = activeAppointments.filter(
      (a) => a.status === "pending"
    ).length;

    return { total, confirmed, cancelled, pending };
  };

  const stats = getAppointmentStats();

  return (
    <div className="appointment-tracker">
      <div className="tracker-header">
        <h2>
          <FaCalendarCheck className="section-icon" />
          Randevu Takibi
        </h2>
        <p>Mevcut randevuları görüntüleyin ve yönetin</p>
      </div>

      {/* Stats Cards */}
      <div className="appointment-stats">
        <div className="stat-card">
          <div className="stat-icon total">
            <FaCalendarAlt />
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Toplam Randevu</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon confirmed">
            <FaCheck />
          </div>
          <div className="stat-content">
            <h3>{stats.confirmed}</h3>
            <p>Onaylandı</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending">
            <FaClock />
          </div>
          <div className="stat-content">
            <h3>{stats.pending}</h3>
            <p>Bekliyor</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon cancelled">
            <FaTimes />
          </div>
          <div className="stat-content">
            <h3>{stats.cancelled}</h3>
            <p>İptal Edildi</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="İsim, e-posta veya telefon ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label>Durum</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Tarih</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              {dateFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Current Appointments List */}
      <div className="appointments-section">
        <div className="section-header">
          <h3>
            <FaCalendarCheck className="section-icon" />
            Aktif Randevular
          </h3>
          <span className="appointment-count">
            ({currentAppointments.length})
          </span>
        </div>

        <div className="appointments-list">
          {isLoading ? (
            <div className="loading">Randevular yükleniyor...</div>
          ) : currentAppointments.length === 0 ? (
            <div className="no-appointments">
              <FaExclamationCircle className="no-data-icon" />
              <p>
                {appointments.length === 0
                  ? "Henüz randevu bulunmuyor"
                  : "Filtrelere uygun aktif randevu bulunamadı"}
              </p>
            </div>
          ) : (
            currentAppointments.map((appointment) => (
              <div key={appointment.id} className="appointment-card">
                <div className="appointment-header">
                  <div className="appointment-date">
                    <FaCalendarAlt className="date-icon" />
                    <div>
                      <div className="date-main">
                        {formatShortDate(appointment.date)}
                      </div>
                      <div className="time-main">
                        <FaClock className="time-icon" />
                        {appointment.time}
                      </div>
                    </div>
                  </div>
                  <div
                    className="appointment-status"
                    style={{
                      backgroundColor: getStatusColor(appointment.status),
                    }}
                  >
                    {getStatusLabel(appointment.status)}
                  </div>
                </div>

                <div className="appointment-body">
                  <div className="user-info">
                    <div className="user-main">
                      <FaUser className="user-icon" />
                      <span className="user-name">
                        {appointment.userInfo?.firstName ||
                          appointment.userData?.firstName ||
                          "İsim"}{" "}
                        {appointment.userInfo?.lastName ||
                          appointment.userData?.lastName ||
                          "Soyisim"}
                      </span>
                    </div>
                    <div className="contact-info">
                      <div className="contact-item">
                        <FaEnvelope className="contact-icon" />
                        <span>
                          {appointment.userInfo?.email ||
                            appointment.userData?.email ||
                            "E-posta yok"}
                        </span>
                      </div>
                      <div className="contact-item">
                        <FaPhone className="contact-icon" />
                        <span>
                          {appointment.userInfo?.phone ||
                            appointment.userData?.phone ||
                            "Telefon yok"}
                        </span>
                      </div>
                      <div className="contact-item">
                        <span className="age-info">
                          <strong>Yaş:</strong>{" "}
                          {appointment.userInfo?.age ||
                            appointment.userData?.age ||
                            "Belirtilmemiş"}
                        </span>
                      </div>
                      <div className="contact-item">
                        <span className="education-info">
                          <strong>Eğitim:</strong>{" "}
                          {getEducationLabel(
                            appointment.userInfo?.education ||
                              appointment.userData?.education
                          )}
                        </span>
                      </div>
                      {appointment.ogrenciNumarasi && (
                        <div className="contact-item">
                          <span className="student-number-info">
                            <strong>Öğrenci No:</strong>{" "}
                            <span style={{ fontWeight: "700", color: "#667eea" }}>
                              {appointment.ogrenciNumarasi}
                            </span>
                          </span>
                        </div>
                      )}
                      {appointment.meetLink && (
                        <div className="contact-item meet-link-item">
                          <FaVideo className="contact-icon meet-icon" />
                          <a
                            href={appointment.meetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="meet-link"
                          >
                            Google Meet'e Katıl
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="appointment-actions">
                    <button
                      className="action-button view"
                      onClick={() => openAppointmentModal(appointment)}
                    >
                      <FaEye />
                    </button>

                    {appointment.status !== "completed" &&
                      appointment.status !== "cancelled" && (
                        <>
                          <button
                            className="action-button complete"
                            onClick={() =>
                              handleCompleteWithDetails(appointment)
                            }
                            disabled={isLoading}
                            title="Randevuyu detaylı bilgilerle tamamla"
                          >
                            <FaCheck />
                          </button>
                          <button
                            className="action-button cancel"
                            onClick={() =>
                              handleStatusUpdate(appointment.id, "cancelled")
                            }
                            disabled={isLoading}
                          >
                            <FaTimes />
                          </button>
                        </>
                      )}

                    <button
                      className="action-button delete"
                      onClick={() => handleDeleteAppointment(appointment.id)}
                      disabled={isLoading}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Past Appointments Section */}
      {pastAppointments.length > 0 && (
        <div className="appointments-section past-appointments">
          <div className="section-header">
            <h3>
              <FaCalendarAlt className="section-icon" />
              Eski Randevular
            </h3>
            <div className="section-controls">
              <span className="appointment-count">
                ({pastAppointments.length})
              </span>
              <button
                className="toggle-button"
                onClick={() => setShowPastAppointments(!showPastAppointments)}
              >
                {showPastAppointments ? "Gizle" : "Göster"}
              </button>
            </div>
          </div>

          {showPastAppointments && (
            <div className="appointments-list">
              {pastAppointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="appointment-card past-appointment"
                >
                  <div className="appointment-header">
                    <div className="appointment-date">
                      <FaCalendarAlt className="date-icon" />
                      <div>
                        <div className="date-main">
                          {formatShortDate(appointment.date)}
                        </div>
                        <div className="time-main">
                          <FaClock className="time-icon" />
                          {appointment.time}
                        </div>
                      </div>
                    </div>
                    <div
                      className="appointment-status"
                      style={{
                        backgroundColor: getStatusColor(appointment.status),
                      }}
                    >
                      {getStatusLabel(appointment.status)}
                    </div>
                  </div>

                  <div className="appointment-body">
                    <div className="user-info">
                      <div className="user-main">
                        <FaUser className="user-icon" />
                        <span className="user-name">
                          {appointment.userInfo?.firstName ||
                            appointment.userData?.firstName ||
                            "İsim"}{" "}
                          {appointment.userInfo?.lastName ||
                            appointment.userData?.lastName ||
                            "Soyisim"}
                        </span>
                      </div>
                      <div className="contact-info">
                        <div className="contact-item">
                          <FaEnvelope className="contact-icon" />
                          <span>
                            {appointment.userInfo?.email ||
                              appointment.userData?.email ||
                              "E-posta yok"}
                          </span>
                        </div>
                        <div className="contact-item">
                          <FaPhone className="contact-icon" />
                          <span>
                            {appointment.userInfo?.phone ||
                              appointment.userData?.phone ||
                              "Telefon yok"}
                          </span>
                        </div>
                        <div className="contact-item">
                          <span className="age-info">
                            <strong>Yaş:</strong>{" "}
                            {appointment.userInfo?.age ||
                              appointment.userData?.age ||
                              "Belirtilmemiş"}
                          </span>
                        </div>
                        <div className="contact-item">
                          <span className="education-info">
                            <strong>Eğitim:</strong>{" "}
                            {getEducationLabel(
                              appointment.userInfo?.education ||
                                appointment.userData?.education
                            )}
                          </span>
                        </div>
                        {appointment.ogrenciNumarasi && (
                          <div className="contact-item">
                            <span className="student-number-info">
                              <strong>Öğrenci No:</strong>{" "}
                              <span style={{ fontWeight: "700", color: "#667eea" }}>
                                {appointment.ogrenciNumarasi}
                              </span>
                            </span>
                          </div>
                        )}
                        {appointment.meetLink && (
                          <div className="contact-item meet-link-item">
                            <FaVideo className="contact-icon meet-icon" />
                            <a
                              href={appointment.meetLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="meet-link"
                            >
                              Google Meet'e Katıl
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="appointment-actions">
                      <button
                        className="action-button view"
                        onClick={() => openAppointmentModal(appointment)}
                      >
                        <FaEye />
                      </button>

                      <button
                        className="action-button delete"
                        onClick={() => handleDeleteAppointment(appointment.id)}
                        disabled={isLoading}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Appointment Detail Modal */}
      {showModal && selectedAppointment && (
        <div className="modal-overlay" onClick={closeAppointmentModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Randevu Detayları</h3>
              <button className="close-button" onClick={closeAppointmentModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h4>Randevu Bilgileri</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Tarih</label>
                    <span>{formatDate(selectedAppointment.date)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Saat</label>
                    <span>{selectedAppointment.time}</span>
                  </div>
                  <div className="detail-item">
                    <label>Durum</label>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusColor(
                          selectedAppointment.status
                        ),
                      }}
                    >
                      {getStatusLabel(selectedAppointment.status)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Kişi Bilgileri</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Ad Soyad</label>
                    <span>
                      {selectedAppointment.userInfo?.firstName ||
                        selectedAppointment.userData?.firstName ||
                        "İsim"}{" "}
                      {selectedAppointment.userInfo?.lastName ||
                        selectedAppointment.userData?.lastName ||
                        "Soyisim"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>E-posta</label>
                    <span>
                      {selectedAppointment.userInfo?.email ||
                        selectedAppointment.userData?.email ||
                        "E-posta yok"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Telefon</label>
                    <span>
                      {selectedAppointment.userInfo?.phone ||
                        selectedAppointment.userData?.phone ||
                        "Telefon yok"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Yaş</label>
                    <span>
                      {selectedAppointment.userInfo?.age ||
                        selectedAppointment.userData?.age ||
                        "Belirtilmemiş"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Eğitim Durumu</label>
                    <span>
                      {getEducationLabel(
                        selectedAppointment.userInfo?.education ||
                          selectedAppointment.userData?.education
                      )}
                    </span>
                  </div>
                  {selectedAppointment.ogrenciNumarasi && (
                    <div className="detail-item">
                      <label>Öğrenci Numarası</label>
                      <span style={{ fontWeight: "700", color: "#667eea", fontSize: "16px" }}>
                        {selectedAppointment.ogrenciNumarasi}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Student Documents Section */}
              {selectedAppointment.ogrenciNumarasi && (
                <div className="detail-section">
                  <h4>Yüklenen Belgeler</h4>
                  {loadingDocuments ? (
                    <div className="documents-loading">
                      <p>Belgeler yükleniyor...</p>
                    </div>
                  ) : studentDocuments && (studentDocuments.okul.length > 0 || studentDocuments.vize.length > 0) ? (
                    <div className="documents-container">
                      {/* School Documents */}
                      {studentDocuments.okul.length > 0 && (
                        <div className="document-category">
                          <h5>Okul Belgeleri</h5>
                          <div className="document-list">
                            {studentDocuments.okul.map((doc, index) => (
                              <div key={index} className="document-item">
                                <span className="document-name">{doc.name}</span>
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="document-link"
                                >
                                  Görüntüle
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Visa Documents */}
                      {studentDocuments.vize.length > 0 && (
                        <div className="document-category">
                          <h5>Vize Belgeleri</h5>
                          <div className="document-list">
                            {studentDocuments.vize.map((doc, index) => (
                              <div key={index} className="document-item">
                                <span className="document-name">{doc.name}</span>
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="document-link"
                                >
                                  Görüntüle
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-documents">
                      <p>Henüz belge yüklenmemiş</p>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                {selectedAppointment.status !== "completed" &&
                  selectedAppointment.status !== "cancelled" && (
                    <>
                      <button
                        className="modal-action-button complete"
                        onClick={() => {
                          closeAppointmentModal();
                          handleCompleteWithDetails(selectedAppointment);
                        }}
                        disabled={isLoading}
                      >
                        <FaCheck />
                        Tamamla
                      </button>
                      <button
                        className="modal-action-button cancel"
                        onClick={() => {
                          handleStatusUpdate(
                            selectedAppointment.id,
                            "cancelled"
                          );
                          closeAppointmentModal();
                        }}
                        disabled={isLoading}
                      >
                        <FaTimes />
                        İptal Et
                      </button>
                    </>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Completion Modal */}
      <AppointmentCompletionModal
        appointment={appointmentToComplete}
        isOpen={showCompletionModal}
        onClose={() => {
          setShowCompletionModal(false);
          setAppointmentToComplete(null);
        }}
        onComplete={handleCompletionSubmit}
        isLoading={isLoading}
        showToast={addToast}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default AppointmentTracker;
